import React from 'react';
import PropTypes from 'prop-types';
import throttle from 'lodash.throttle';
import cx from 'classnames';
import { Button, Modal, Slider, Radio } from 'antd';
import { EditorElementPopover } from '../EditorElementPopover';
import {
  SwapOutlined,
  DeleteOutlined,
  PlusOutlined,
} from '@ant-design/icons';

export class PBlock extends React.Component {
  constructor(props) {
    super(props);
    this.popoverRef = React.createRef();
  }

  state = {
    showBtn: false,
  };

  static propTypes = {
    height: PropTypes.string, // full | tall (80%) | fifty (50%) | band (20%) | content (default)
    width: PropTypes.string, // '' | 'fluid' (default)
    pad: PropTypes.string, // hi | md (default) | lo | none

    backgroundColor: PropTypes.string, // bgLight (default) | bgDark | bgLightAlt | bgDarkAlt
    backgroundMediaUrlsafe: PropTypes.string,
    backgroundTint: PropTypes.number, // percentage value between 0 (default) and 90

    className: PropTypes.string,
    style: PropTypes.object,

    colorScheme: PropTypes.object,
    children: PropTypes.oneOfType([
      PropTypes.array,
      PropTypes.object,
    ]),

    address: PropTypes.array,
    updateNode: PropTypes.func,
    invokeGallery: PropTypes.func,
    srcForMedia: PropTypes.func,
    publish: PropTypes.bool,
  };

  static defaultProps = {
    height: 'content',
    width: '',
    pad: 'md',
    backgroundMediaUrlsafe: null,
    backgroundColor: 'bgLight',
    backgroundTint: 0,
    publish: false,
  };

  invokeGallery = () => {
    this.popoverRef.current.dismiss();
    this.props.invokeGallery(this.props.address, 'backgroundMediaUrlsafe', 'lg');
  };

  backgroundColorSelected = (colorName) => {
    if (colorName !== this.props.backgroundColor) {
      this.props.updateNode(this.props.address, { backgroundColor: colorName });
    }
  };

  throttleBackgroundTintChanged = throttle((value) => {
    this.props.updateNode(this.props.address, { backgroundTint: value });
  }, 900);

  makePill = (colorName) => (
    <span
      onClick={() => this.backgroundColorSelected(colorName)}
      className={cx(
        'bgpill',
        { selected: colorName === this.props.backgroundColor }
      )}
      style={{ backgroundColor: this.props.colorScheme[colorName] }}
    />
  );

  containerWidthChagned = (e) => {
    const nextWidth = e.target.value === 'fluid' ? 'fluid' : '';
    this.props.updateNode(this.props.address, { width: nextWidth });
  };

  removeBackgroundImageConfirmed = () => {
    this.props.updateNode(this.props.address, { backgroundMediaUrlsafe: null });
  };

  removeBackgroundImage = () => {
    this.popoverRef.current.dismiss();
    Modal.confirm({
      title: 'Remove background image?',
      content: 'Images removed from this page will be available in your gallery. ' +
        'So you can add it back in case you change your mind. ' +
        'To delete it permanently, visit your gallery.',
      okText: 'Remove',
      okType: 'danger',
      cancelText: 'Never mind',
      onOk: this.removeBackgroundImageConfirmed,
    });
  };

  popoverContent = () => {
    const { backgroundMediaUrlsafe, backgroundTint, width } = this.props;
    return (
      <React.Fragment>
        { backgroundMediaUrlsafe !== null ? (
          <React.Fragment>
            <div>
              <div className="label">
                <div className="main">Background Image</div>
              </div>
            </div>
            <div className="text-center mb-2">
              <img
                src={this.props.srcForMedia(backgroundMediaUrlsafe, 'thumb')}
                alt="Preview thumbnail"
              />
            </div>
            <div className="d-flex flex-row">
              <Button
                type="primary" icon={<SwapOutlined />} block
                className="flex-grow-1 mr-2"
                onClick={this.invokeGallery}
              >
                Replace
              </Button>
              <Button
                type="danger" icon={<DeleteOutlined />} block className="flex-grow-1 ml-2"
                onClick={this.removeBackgroundImage}
              >
                Remove
              </Button>
            </div>

            <div className="divider" />

            <div className="label">
              <div className="main">Overlay Tint</div>
              <div className="help">Helps foreground text stand out.</div>
            </div>
            <div>
              <Slider
                min={0} max={80} defaultValue={backgroundTint}
                onChange={this.throttleBackgroundTintChanged}
              />
            </div>

            <div className="divider" />

          </React.Fragment>
        ) : (
          <React.Fragment>
            <Button
              type="primary" icon={<PlusOutlined />} block
              onClick={this.invokeGallery}
            >
              Add background image
            </Button>
            <div className="divider" />
          </React.Fragment>
        ) }

        <div className="label">
          <div className="main">Solid background</div>
        </div>

        <div className="bgpillContainer mb-2">
          {this.makePill('bgLight')}
          {this.makePill('bgLightAlt')}
          {this.makePill('bgDark')}
          {this.makePill('bgDarkAlt')}
          {this.makePill('primary')}
          {this.makePill('accent1')}
          {this.makePill('accent2')}
          {this.makePill('accent3')}
        </div>

        <div className="divider" />

        <div className="label">
          <div className="main">Wide-screen fluidity</div>
          <div className="help">Whether content extends edge-to-edge or stays centered.</div>
        </div>

        <Radio.Group
          defaultValue={width === 'fluid' ? 'fluid' : 'box'}
          buttonStyle="solid" onChange={this.containerWidthChagned}
        >
          <Radio.Button value="box">Boxed in</Radio.Button>
          <Radio.Button value="fluid">Stretched</Radio.Button>
        </Radio.Group>

      </React.Fragment>
    );
  };

  onMouseOver = (e) => {
    this.setState({ showBtn: true });
    e.stopPropagation();
  };

  onMouseOut = (e) => {
    this.setState({ showBtn: false });
    e.stopPropagation();
  };

  getSolidColor = (c) => {
    const { colorScheme } = this.props;
    const color = (typeof c === 'undefined') ? 'bgLight' : c;
    return colorScheme[color];
  };

  render() {
    const { height, width, pad, backgroundColor, backgroundMediaUrlsafe, backgroundTint, publish } = this.props;

    let containerClass = 'container';
    if (width === 'fluid') {
      containerClass = 'container-fluid';
    }
    let heightClass = null;
    if (height === 'full') {
      heightClass = 'full-height';
    }
    else if (height === 'tall') {
      heightClass = 'tall-height';
    }
    const padClass = `pad-${pad}`;

    let backgroundSrc = null;
    let backgroundImageStyle = {};
    if (backgroundMediaUrlsafe !== null) {
      backgroundSrc = this.props.srcForMedia(backgroundMediaUrlsafe, 'lg');
    }
    if (backgroundSrc !== null) {
      backgroundImageStyle = { backgroundImage: `url(${backgroundSrc})` };
    }
    const styleInEditor = publish ? {} : {
      backgroundColor: this.getSolidColor(backgroundColor),
    };

    return (
      <div
        className={cx(
          'pblock',
        )}
        onMouseOver={this.onMouseOver}
        onFocus={this.onMouseOver}
        onMouseOut={this.onMouseOut}
        onBlur={this.onMouseOut}
      >
        <div
          className={cx(
            'background',
            `bgcolor-${backgroundColor}`
          )}
          style={{
            ...this.props.style,
            ...styleInEditor,
            ...backgroundImageStyle,
          }}
        >
          <div
            className="tint"
            style={{
              ...this.props.style,
              backgroundColor: this.getSolidColor(backgroundColor),
              opacity: (backgroundTint / 100),
            }}
          />
        </div>

        <div
          className={cx(
            containerClass,
            heightClass,
            this.props.className,
            padClass,
          )}
        >
          { this.props.children }
        </div>

        <EditorElementPopover
          ref={this.popoverRef}
          usecase="block"
          showBtn={this.state.showBtn}
          btnLabel="block"
          title="Block appearance"
          content={this.popoverContent()}
          placement="bottomRight"
          publish={publish}
        />

      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./scripts/components/editor/elements/PBlock.jsx